import { useMatch } from 'react-router-dom';
import { useState, type ReactNode } from 'react';

import {
  Button,
  cn,
  IconLucide,
  ScrollArea,
  ScrollBar
} from '@yakit/ui-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast
} from '@yakit/ui-components';
import { isAxiosError } from 'axios';

import { useVDLStore } from '@VDL/store/VDLstore';
import { putLevel0Data } from '@VDL/api';
import { Save } from 'lucide-react';

export type OnToggleEdit = (isEdit: boolean) => void;

interface VDLEditToolbarProps {
  isEdit: boolean;
  onToggleEdit?: OnToggleEdit;
  editLabel?: string;
}

export const VDLEditToolbar = ({
  isEdit,
  onToggleEdit,
  editLabel = 'Редактировать'
}: VDLEditToolbarProps) => {
  const { toSaveLevel0Data, setToSaveLevel0Data } = useVDLStore();
  const match = useMatch('/:level0Code');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);

    try {
      if (match) {
        alert('Сохраняем object tree');
      } else {
        await toSaveLevel0Data.forEach(
          async (level0Data) => await putLevel0Data(undefined, level0Data)
        );

        setShowModal(false);
        setToSaveLevel0Data([]);
        toast.success('Успешно сохранено');
      }
    } catch (error) {
      if (isAxiosError(error)) console.log(error);
      toast.error('Ошибка при сохранении');
    } finally {
      setLoading(false);
      if (typeof onToggleEdit === 'function') onToggleEdit(false);
    }
  };

  const handleToggleEdit = (value: boolean) => {
    if (typeof onToggleEdit === 'function') onToggleEdit(value);
  };

  return (
    <>
      <GroupedButtonsWrapper className="hidden md:inline-flex">
        {!isEdit && (
          <Button
            size="sm"
            variant="outline"
            className="cursor-pointer gap-2"
            onClick={() => handleToggleEdit(true)}
          >
            {editLabel}
            <IconLucide name="Pencil" className="h-4 w-4" />
          </Button>
        )}
        {isEdit && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="cursor-pointer"
              onClick={() => handleToggleEdit(false)}
            >
              Отмена
            </Button>
            <Button
              size="sm"
              className="cursor-pointer"
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              Сохранить
            </Button>
          </>
        )}
      </GroupedButtonsWrapper>
      {isEdit && (
        <div className="space-x-2">
          <Button
            className="md:hidden"
            size="icon"
            onClick={() => setShowModal(true)}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            className="md:hidden"
            variant="outline"
            size="icon"
            onClick={() => handleToggleEdit(false)}
          >
            <IconLucide name="X" className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!isEdit && (
        <Button
          className="md:hidden"
          size="icon"
          onClick={() => handleToggleEdit(true)}
        >
          <IconLucide name="SquarePen" className="h-4 w-4" />
        </Button>
      )}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите сохранение</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите сохранить изменения?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex w-full justify-between">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Отмена
              </Button>
              <Button onClick={handleSave}>Сохранить</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const GroupedButtonsWrapper = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div>
    <ScrollArea>
      <div
        className={cn('bg-muted inline-flex gap-2 rounded-md p-2', className)}
      >
        {children}
      </div>
      <ScrollBar className="h-2" orientation="horizontal" />
    </ScrollArea>
  </div>
);
